import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { environmentNotice } from 'app/core/environment/build-features';

/**
 * Class put on `<body>` while the bar is on screen, so global styles that
 * assume the viewport starts at y=0 can account for it. Only the Fuse loading
 * bar does today — see the stylesheet.
 */
const BODY_CLASS = 'console-has-env-banner';

/**
 * The bar pinned above the entire console, naming the environment this build
 * talks to.
 *
 * It sits in `AppComponent`, outside the router outlet, so it covers every
 * layout — including the `empty` one behind the sign-in page. That is
 * deliberate: knowing which console you are handing credentials to is worth
 * more before signing in than after.
 *
 * Like `SectionUnavailableComponent`, this carries **no copy of its own**. The
 * label comes from `environment.environmentNotice`, which is `null` in the
 * release build — so production renders nothing and the wording is not in the
 * bundle at all.
 *
 * It takes its own row rather than floating over the page. An overlay would
 * cover the top of whatever is beneath it (the console header, the sign-in
 * card) at every width; a row costs 28px once and leaves every layout below it
 * to size itself as before.
 */
@Component({
  selector: 'environment-banner',
  templateUrl: './environment-banner.component.html',
  styleUrls: ['./environment-banner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class EnvironmentBannerComponent {
  /** The environment's name, or `null` in a release build (renders nothing). */
  readonly label = environmentNotice();

  constructor() {
    if (!this.label) {
      return;
    }

    // Same approach `LayoutComponent` uses for the OS class: a marker on
    // `<body>` that global styles outside this component's view can key on.
    // The bar is decided at build time and never removed, so there is nothing
    // to tear down.
    inject(DOCUMENT).body.classList.add(BODY_CLASS);
  }
}
